import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  LeftOutlined, 
  ShareAltOutlined, 
  EditOutlined,
  UserOutlined,
  ManOutlined,
  WomanOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import { Avatar, Tabs, Spin, Alert, Tag, Progress, Divider } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './MobilePlayerDetail.css';
import RatingHistoryChart from './RatingHistoryChart';
import DoublesBasicStats from '../statistics/DoublesBasicStats';
import PartnerStats from '../statistics/PartnerStats';
import OpponentStats from '../statistics/OpponentStats';
import moment from 'moment';

/**
 * 移动端球员详情页组件
 * 展示球员的详细信息、比赛记录和数据统计
 */
const MobilePlayerDetail = () => {
  const { id, zone } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [player, setPlayer] = useState(null);
  const [recentMatches, setRecentMatches] = useState([]);
  const [activeRatingType, setActiveRatingType] = useState('singles');
  const [ratingHistory, setRatingHistory] = useState({ singles: [], doubles: [] });
  const [opponentAnalysis, setOpponentAnalysis] = useState(null);
  const [doublesStatistics, setDoublesStatistics] = useState(null);
  const [doublesStatsLoading, setDoublesStatsLoading] = useState(false);

  // API 基础URL - 动态获取，根据环境自动适配
  const baseUrl = getApiBaseUrl();

  useEffect(() => {
    const fetchPlayerData = async () => {
      try {
        // 验证ID和zone是否有效
        if (!id || id === 'undefined') {
          throw new Error('无效的球员ID');
        }
        if (!zone) {
          throw new Error('缺少区域参数');
        }

        setLoading(true);
        console.log('正在获取球员信息，ID:', id, '区域:', zone);
        
        const [playerRes, matchesRes, winRateRes, ratingHistoryRes, opponentRes] = await Promise.all([
          axios.get(`${baseUrl}/api/${zone}/players/${id}`),
          axios.get(`${baseUrl}/api/${zone}/players/${id}/matches?limit=20`),
          axios.get(`${baseUrl}/api/${zone}/players/${id}/win-rate`),
          axios.get(`${baseUrl}/api/${zone}/players/${id}/rating-history`),
          axios.get(`${baseUrl}/api/${zone}/players/${id}/opponent-analysis`)
        ]);

        // 获取双打统计数据（包含搭档统计）
        setDoublesStatsLoading(true);
        try {
          const statsRes = await axios.get(`${baseUrl}/api/${zone}/players/${id}/statistics/doubles`, {
            params: {
              includePartners: true,
              includeOpponents: true  // 包含对手统计
            }
          });
          if (statsRes.data.success) {
            setDoublesStatistics(statsRes.data.data);
          }
        } catch (err) {
          console.error('获取双打统计失败:', err);
        } finally {
          setDoublesStatsLoading(false);
        }

        console.log('球员信息响应:', playerRes.data);
        console.log('比赛记录响应:', matchesRes.data);
        console.log('胜率分析响应:', winRateRes.data);
        console.log('积分历史响应:', ratingHistoryRes.data);
        console.log('对手分析响应:', opponentRes.data);

        if (playerRes.data.success) {
          // 合并胜率分析数据到球员数据中
          const playerData = playerRes.data.data;
          if (winRateRes.data.success) {
            playerData.winRateAnalysis = winRateRes.data.data;
          }
          // 确保stats字段存在
          if (!playerData.stats) {
            playerData.stats = {
              singles: {
                rating: 1500,
                matches: 0,
                wins: 0,
                losses: 0,
                tier: 'NEW'
              },
              doubles: {
                rating: 1500,
                matches: 0,
                wins: 0,
                losses: 0,
                tier: 'NEW'
              }
            };
          }
          setPlayer(playerData);
        } else {
          throw new Error(playerRes.data.message || '获取球员信息失败');
        }

        if (matchesRes.data.success) {
          const matchHistory = matchesRes.data.data.matchHistory || [];
          console.log('比赛记录详情:', JSON.stringify(matchHistory, null, 2));
          setRecentMatches(matchHistory);
        } else {
          console.warn('获取比赛记录失败:', matchesRes.data.message);
        }

        if (ratingHistoryRes.data.success) {
          setRatingHistory(ratingHistoryRes.data.data);
        } else {
          console.warn('获取积分历史失败:', ratingHistoryRes.data.message);
        }

        if (opponentRes.data.success) {
          setOpponentAnalysis(opponentRes.data.data);
        } else {
          console.warn('获取对手分析失败:', opponentRes.data.message);
        }

        setError(null);
      } catch (err) {
        console.error('获取球员详情失败:', err);
        const errorMessage = err.response?.data?.message || err.message || '获取数据失败';
        console.error('错误详情:', {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          message: errorMessage
        });
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (id && zone) {
      fetchPlayerData();
    } else {
      setError('无效的球员ID或区域参数');
    }
  }, [id, zone]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center p-4">
          <div className="text-lg">加载中...</div>
          <div className="text-sm text-gray-500">正在获取球员信息</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-lg">
          <div className="text-red-500 text-lg font-bold mb-2">加载失败</div>
          <div className="text-gray-600 mb-4">{error}</div>
          <button
            onClick={() => navigate(-1)}
            className="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition-colors"
          >
            返回上一页
          </button>
        </div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-lg">
          <div className="text-lg font-bold mb-2">未找到球员</div>
          <div className="text-gray-600 mb-4">
            找不到ID为 {id} 的球员信息
          </div>
          <button
            onClick={() => navigate(-1)}
            className="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition-colors"
          >
            返回上一页
          </button>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    navigate(-1);
  };

  const handleShare = () => {
    console.log('Share player:', player.name);
  };

  const handleEdit = () => {
    navigate(`/badminton/${zone}/players/${id}/edit`);
  };

  // 处理积分走势数据
  const formatRatingData = (data, type) => {
    if (!data || !data[type] || !data[type].history) return [];
    return data[type].history.map((item, index) => ({
      ...item,
      matchIndex: `第${data[type].history.length - index}场`,
      tooltipContent: `对手: ${item.opponent}\n变化: ${item.change >= 0 ? '+' : ''}${item.change}`
    }));
  };

  const getWinRateColorClass = (winRate) => {
    if (winRate >= 70) return 'high';
    if (winRate >= 50) return 'medium'; 
    return winRate > 0 ? 'low' : '';
  };

  // 获取单打对手信息
  const getSinglesOpponent = (match) => {
    if (match.opponentName) return match.opponentName;
    if (match.opponent) {
      return typeof match.opponent === 'object' ? match.opponent.name || '未知' : match.opponent;
    }
    return '未知对手';
  };

  // 获取双打搭档信息
  const getPartnerInfo = (match) => {
    if (match.partnerName) return match.partnerName;
    if (match.partner) {
      return typeof match.partner === 'object' ? match.partner.name || '未知' : match.partner;
    }
    return '未知搭档';
  };

  // 获取双打对方队伍信息
  const getOpposingTeamInfo = (match) => {
    if (match.opponentNames && Array.isArray(match.opponentNames)) {
      return match.opponentNames.join(' / ');
    }
    
    if (match.opponents) {
      if (Array.isArray(match.opponents)) {
        const names = match.opponents.map(opp => 
          typeof opp === 'object' ? (opp.name || '未知') : opp
        );
        return names.join(' / ');
      }
      if (typeof match.opponents === 'string') {
        return match.opponents;
      }
      // 如果是其他格式尝试转换
      return JSON.stringify(match.opponents).replace(/[{}"[\]]/g, '').replace(/:/g, ': ');
    }
    
    if (match.opponent) {
      return typeof match.opponent === 'object' ? match.opponent.name || '未知' : match.opponent;
    }
    
    return '未知对手';
  };

  return (
    <div className="mobile-player-detail">
      {/* 导航栏 */}
      <div className="nav-bar">
        <div className="nav-back" onClick={handleBack}>
          <LeftOutlined /> 返回
        </div>
        <div className="nav-title">球员详情</div>
        <div className="nav-actions">
          <div className="nav-action-item nav-action-edit" onClick={handleEdit} title="编辑">
            <EditOutlined style={{ fontSize: '18px' }} />
            <span className="nav-action-text">编辑</span>
          </div>
          <div className="nav-action-item" onClick={handleShare} title="分享">
            <ShareAltOutlined style={{ fontSize: '18px' }} />
          </div>
        </div>
      </div>

      {/* 基本信息 */}
      <div className="player-info">
        <div className="player-info-container">
          <h2 className="player-name">{player.name}</h2>
          <div className={`player-gender ${player.gender === 'MALE' ? 'male' : 'female'}`}>
            {player.gender === 'MALE' ? <ManOutlined /> : <WomanOutlined />}
            <span className="tag-text">{player.gender === 'MALE' ? '男生' : '女生'}</span>
          </div>
        </div>
      </div>

      {/* 积分展示区域 */}
      <div className="rating-section">
        {/* 单打积分 */}
        <div 
          className={`rating-item singles ${activeRatingType === 'singles' ? 'active' : ''}`}
          onClick={() => setActiveRatingType('singles')}
        >
          <div className="rating-type">
            <div className="rating-type-icon singles"></div>
            <div className="rating-label">单打</div>
          </div>
          <div className="rating-main-info">
            <div className="rating-value">{player.stats?.singles?.rating || 1500}</div>
            <div className={`rating-tier ${player.stats?.singles?.tier?.toLowerCase() || 'new'}`}>
              {player.stats?.singles?.tier || 'NEW'}
            </div>
          </div>
          <div className="rating-stats">
            {(player.winRateAnalysis?.singles?.overall?.wins || 0)}胜{(player.winRateAnalysis?.singles?.overall?.losses || 0)}负
          </div>
        </div>

        {/* 双打积分 */}
        <div 
          className={`rating-item doubles ${activeRatingType === 'doubles' ? 'active' : ''}`}
          onClick={() => setActiveRatingType('doubles')}
        >
          <div className="rating-type">
            <div className="rating-type-icon doubles"></div>
            <div className="rating-label">双打</div>
          </div>
          <div className="rating-main-info">
            <div className="rating-value">{player.stats?.doubles?.rating || 1500}</div>
            <div className={`rating-tier ${player.stats?.doubles?.tier?.toLowerCase() || 'new'}`}>
              {player.stats?.doubles?.tier || 'NEW'}
            </div>
          </div>
          <div className="rating-stats">
            {(player.winRateAnalysis?.doubles?.overall?.wins || 0)}胜{(player.winRateAnalysis?.doubles?.overall?.losses || 0)}负
          </div>
        </div>
      </div>

      {/* 积分走势图 */}
      <div className="rating-chart-section">
        <RatingHistoryChart 
          playerId={id} 
          zone={zone}
        />
      </div>

      {/* 双打基础统计 */}
      {activeRatingType === 'doubles' && (
        <div className="doubles-statistics-section">
          <DoublesBasicStats 
            statistics={doublesStatistics}
            loading={doublesStatsLoading}
          />
          {/* 搭档统计 */}
          {doublesStatistics && doublesStatistics.doubles && doublesStatistics.doubles.partners && (
            <div className="partner-statistics-section">
              <PartnerStats 
                partners={doublesStatistics.doubles.partners}
                loading={doublesStatsLoading}
              />
            </div>
          )}
          {/* 对手统计 */}
          {doublesStatistics && doublesStatistics.doubles && doublesStatistics.doubles.opponents !== undefined && (
            <div className="opponent-statistics-section">
              <OpponentStats 
                opponents={doublesStatistics.doubles.opponents}
                loading={doublesStatsLoading}
              />
            </div>
          )}
        </div>
      )}

      {/* 双打搭档历史 */}
      {player.stats?.doubles?.partners && player.stats.doubles.partners.length > 0 && (
        <div className="partners-section">
          <div className="section-title">常用搭档</div>
          <div className="partners-list">
            {player.stats.doubles.partners
              .sort((a, b) => b.matches - a.matches)
              .slice(0, 3)
              .map(partner => (
                <div key={partner.partnerId} className="partner-item">
                  <div className="partner-name">{partner.playerName}</div>
                  <div className="partner-stats">
                    {partner.matches}场 {partner.wins}胜
                    ({((partner.wins/partner.matches)*100).toFixed(1)}%)
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* 最近比赛记录 */}
      {recentMatches.length > 0 && (
        <div className="recent-matches-section">
          <div className="section-title">最近比赛</div>
          <div className="matches-list">
            {recentMatches
              .sort((a, b) => new Date(b.date) - new Date(a.date))  // 按日期降序排序，确保最新的在前面
              .map((match, index) => (
              <div 
                key={match.matchId || index} 
                className={`match-item ${match.isWinner ? 'win-match' : 'loss-match'}`}
              >
                <div className="match-header">
                  <div className="match-type-result">
                    <div className={`match-type ${match.type === 'SINGLES' ? 'singles' : 'doubles'}`}>
                      {match.type === 'SINGLES' ? '单打' : '双打'}
                    </div>
                    <div className="match-result">
                      {match.isWinner ? 
                        <span className="win">胜</span> : 
                        <span className="loss">负</span>
                      }
                    </div>
                  </div>
                  <div className="match-date">
                    {moment(match.date).format('YYYY/MM/DD')}
                  </div>
                </div>
                
                <div className="match-content">
                  {match.type === 'SINGLES' ? (
                    <div className="match-opponent-singles">
                      <span className="opponent-label">VS</span>
                      <span className="opponent-name">{getSinglesOpponent(match)}</span>
                      <span className="match-score">{match.scores}</span>
                    </div>
                  ) : (
                    <div className="match-opponent-doubles">
                      <div className="team-info">
                        <span className="team-label">我方:</span>
                        <span className="team-member">{player.name} / {getPartnerInfo(match)}</span>
                      </div>
                      <div className="team-info">
                        <span className="team-label">对方:</span>
                        <span className="team-member">{getOpposingTeamInfo(match)}</span>
                        <span className="match-score">{match.scores}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MobilePlayerDetail;
